/**
 * Map Assets Catalog
 * 
 * Defines all static map assets like trees, rocks, buildings, etc.
 * These are placeable objects with collision support.
 * 
 * HOW TO ADD NEW MAP ASSETS:
 * 1. Place your GLB file in public/assets/map/
 * 2. Add an entry below with appropriate collision settings
 * 3. Use MapAssetManager to place instances in your scene
 * 
 * COLLISION MODES:
 * - NONE: No collision (purely visual)
 * - BOUNDING_BOX: Auto-generated box from model bounds
 * - BOUNDING_SPHERE: Auto-generated sphere from model bounds
 * - CAPSULE: Auto-generated capsule (good for tall objects)
 * - CYLINDER: Auto-generated cylinder (good for tree trunks)
 * - MESH: Exact mesh collision (expensive, use sparingly)
 * - CONVEX_HULL: Convex hull approximation (moderate cost)
 * - CUSTOM: Specify exact dimensions
 * 
 * COMPOUND COLLISION:
 * For rigged models with multiple parts (trees with branches, characters, etc.),
 * enable useCompoundCollision to create separate collision shapes for each mesh part.
 * Use collisionMeshFilter to only include specific parts (e.g., "trunk|branch").
 * 
 * DEBUG VISUALIZATION:
 * Set debugVisualize: true to see collision shapes rendered as wireframes.
 * You can also call mapAssetManager.setDebugMode(true) globally.
 */

import { 
    MapAssetDefinition, 
    MapAssetCategory, 
    CollisionMode,
    CollisionGroups,
} from "@engine/index";
import { PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";

/**
 * Map assets catalog - all placeable map objects
 */
export const MapAssets: Record<string, MapAssetDefinition> = {
    // ============================================
    // VEGETATION - Trees, bushes, etc.
    // ============================================

    /**
     * Tree - Basic tree model
     * 
     * Can be placed multiple times on the map.
     * Uses custom cylinder collision for efficient trunk collision.
     * 
     * The tree model's bounding box and collision are automatically
     * computed from the GLB file. For rigged trees with separate
     * parts (trunk, branches, leaves), enable compound collision.
     * 
     * NOTE: Custom dimensions are in MODEL UNITS (before scale is applied)
     * With scale: 10.0, these values are multiplied by 10 in world space.
     */
    tree: {
        id: "tree",
        name: "Tree",
        modelPath: "map/tree.glb",
        category: MapAssetCategory.VEGETATION,
        scale: 10.0, // Adjusted: 10x bigger
        collision: {
            // Use CUSTOM mode for precise trunk collision
            // This avoids using the full bounding box which includes leaves
            mode: CollisionMode.CUSTOM,
            customDimensions: {
                shapeType: PhysicsShapeType.CYLINDER,
                // Dimensions are in MODEL UNITS - will be multiplied by scale (10.0)
                radius: 0.15,  // 0.15 * 10 = 1.5m trunk radius in world space (increased for collision)
                height: 1.5,  // 1.5 * 10 = 15m trunk height in world space
                offset: { x: 0, y: 0.75, z: 0 }, // 0.75 * 10 = 7.5m center offset in world space
            },
            
            // Show debug visualization of the collision shape (enable for debugging)
            debugVisualize: false, // Set to true to see collision shapes
            
            friction: 0.8,
            restitution: 0.1,
            collisionGroup: CollisionGroups.VEGETATION,
            collisionMask: CollisionGroups.PLAYER | CollisionGroups.NPC | CollisionGroups.ENTITY,
        },
        castShadows: true,
        receiveShadows: true,
        metadata: {
            category: "vegetation",
            type: "tree",
            description: "A tree that can be placed throughout the map",
        },
    },

    /**
     * Tree with compound collision - Example for rigged tree models
     * 
     * Use this template for trees with separate mesh parts for
     * trunk, branches, foliage, etc. Each part will get its own
     * collision shape based on its bounding box.
     */
    tree_compound: {
        id: "tree_compound",
        name: "Tree (Compound Collision)",
        modelPath: "map/tree.glb",
        category: MapAssetCategory.VEGETATION,
        scale: 1.0,
        collision: {
            mode: CollisionMode.BOUNDING_BOX, // Base mode for fallback
            
            // Enable compound collision - creates physics for each mesh part
            useCompoundCollision: true,
            
            // Filter which mesh parts get collision (regex)
            // Common patterns: "trunk", "branch", "stem", "body"
            // Leave undefined to include all parts
            collisionMeshFilter: "trunk|stem|body",
            
            // Enable to see collision shapes for debugging
            debugVisualize: false,
            
            friction: 0.8,
            restitution: 0.1,
            collisionGroup: CollisionGroups.VEGETATION,
            collisionMask: CollisionGroups.PLAYER | CollisionGroups.NPC | CollisionGroups.ENTITY,
        },
        castShadows: true,
        receiveShadows: true,
        metadata: {
            category: "vegetation",
            type: "tree",
            description: "A tree with compound collision for each part",
            isRigged: true,
        },
    },

    // ============================================
    // TEMPLATE: Copy for new map assets
    // ============================================
    /*
    assetId: {
        id: "assetId",
        name: "Asset Name",
        modelPath: "map/asset.glb",
        category: MapAssetCategory.VEGETATION, // or ROCK, STRUCTURE, PROP, etc.
        scale: 1.0,
        collision: {
            mode: CollisionMode.BOUNDING_BOX, // or CYLINDER, CUSTOM, etc.
            
            // For rigged models with parts:
            // useCompoundCollision: true,
            // collisionMeshFilter: "body|arm|leg",
            
            // Debug visualization:
            // debugVisualize: true,
            
            friction: 0.5,
            restitution: 0.2,
            collisionGroup: CollisionGroups.ENTITY,
            collisionMask: CollisionGroups.ALL,
        },
        castShadows: true,
        receiveShadows: true,
        metadata: {
            description: "Description of the asset",
        },
    },
    */
};

/**
 * Get map asset definition by ID
 */
export function getMapAssetDefinition(id: string): MapAssetDefinition | null {
    return MapAssets[id] || null;
}

/**
 * Get all map asset IDs
 */
export function getAllMapAssetIds(): string[] {
    return Object.keys(MapAssets);
}

/**
 * Get map assets by category
 */
export function getMapAssetsByCategory(category: MapAssetCategory): MapAssetDefinition[] {
    return Object.values(MapAssets).filter(asset => asset.category === category);
}

/**
 * Register all map assets with a MapAssetManager
 */
export function registerAllMapAssets(
    manager: import("@engine/index").MapAssetManager
): void {
    Object.values(MapAssets).forEach(asset => {
        manager.registerAsset(asset);
    });
    console.log(`Registered ${Object.keys(MapAssets).length} map assets`);
}
